import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

export interface BibleStudy {
  id?: number;
  date: string;
  topic: string;
  men_count: number;
  women_count: number;
  notes: string;
  group_type?: string;
  expected_attendees?: number;
  actual_attendees_count?: number;
  responsible?: number;
  responsible_name?: string;
  status?: string;
  is_active?: boolean;
}

interface BibleStudyFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  studyToEdit?: BibleStudy | null;
}

const BibleStudyForm: React.FC<BibleStudyFormProps> = ({ onSuccess, onCancel, studyToEdit }) => {
  const { addToast } = useToast();
  const [date, setDate] = useState('');
  const [topic, setTopic] = useState('');
  const [menCount, setMenCount] = useState(0);
  const [womenCount, setWomenCount] = useState(0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (studyToEdit) {
      setDate(studyToEdit.date);
      setTopic(studyToEdit.topic);
      setMenCount(studyToEdit.men_count);
      womenCount: setWomenCount(studyToEdit.women_count);
      setNotes(studyToEdit.notes);
    } else {
      setDate(new Date().toISOString().split('T')[0]);
      setTopic('');
      setMenCount(0);
      setWomenCount(0);
      setNotes('');
    }
  }, [studyToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const studyData = { date, topic, men_count: menCount, women_count: womenCount, notes };

    try {
      if (studyToEdit && studyToEdit.id) {
        await api.patch(`bible-studies/${studyToEdit.id}/`, studyData);
        addToast('Estudo Bíblico atualizado!', 'success');
      } else {
        await api.post('bible-studies/', studyData);
        addToast('Registro de estudo bíblico salvo!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save Bible study', error);
      addToast('Erro ao salvar registro.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{studyToEdit ? '📖 Editar Registro' : '✨ Novo Estudo Bíblico'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Informações do Estudo" icon="📜" />
        <div className="form-group">
          <label>Tema / Tópico Abordado</label>
          <input 
            type="text" 
            value={topic} 
            onChange={(e) => setTopic(e.target.value)} 
            placeholder="Ex: O papel da família na educação cristã"
            required 
          />
        </div>

        <div className="form-group">
          <label>Data do Estudo</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Participação e Engajamento" icon="👥" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Nº de Homens</label>
            <input 
              type="number" 
              value={menCount} 
              onChange={(e) => setMenCount(parseInt(e.target.value))} 
              min="0" 
              required 
            />
          </div>
          <div className="form-group">
            <label>Nº de Mulheres</label>
            <input 
              type="number" 
              value={womenCount} 
              onChange={(e) => setWomenCount(parseInt(e.target.value))} 
              min="0" 
              required 
            />
          </div>
        </div>

        <div className="form-group">
          <label>Observações / Insights</label>
          <textarea 
            value={notes} 
            onChange={(e) => setNotes(e.target.value)} 
            placeholder="Anotações sobre a participação ou pontos chave..."
            style={{ height: '80px' }}
          />
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Registro'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BibleStudyForm;
